import { useEffect, useLayoutEffect, useMemo, useRef } from "react";

interface ParticleBurstTextProps {
  /** 0 → 1 : 0 = fully assembled text, 1 = fully burst apart & invisible */
  progress: number;
  className?: string;
  children: React.ReactNode;
}

interface Particle {
  ox: number;
  oy: number;
  dx: number;
  dy: number;
  rot: number;
  delay: number;
  size: number;
  alpha: number;
}

// Deterministic pseudo-random so particle positions are stable across renders.
const rand = (seed: number) => {
  const s = Math.sin(seed) * 43758.5453;
  return s - Math.floor(s);
};

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

/**
 * Headline that stays crisp when progress = 0, then explodes into ~140
 * particles that fly outward + fade as progress → 1.
 *
 * Performance note: particles are rendered ONCE and then animated imperatively
 * via refs + a rAF loop that eases toward the current `progress` target. This
 * avoids React re-reconciling 140 DOM nodes on every scroll tick — which was
 * the source of the stutter — and keeps the effect on the compositor thread.
 */
export default function ParticleBurstText({
  progress,
  className,
  children,
}: ParticleBurstTextProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const textLayerRef = useRef<HTMLDivElement>(null);
  const particleLayerRef = useRef<HTMLDivElement>(null);
  const particleRefs = useRef<(HTMLSpanElement | null)[]>([]);

  // Latest requested progress (from prop). Written on each render.
  const targetRef = useRef(clamp01(progress));
  // Current smoothed progress (what we actually draw). Approaches target each frame.
  const currentRef = useRef(clamp01(progress));

  // Pre-compute stable particle offsets. Positioned in normalized (-1 → 1) coords,
  // scaled to the container size at render time.
  const particles = useMemo<Particle[]>(() => {
    const count = 140;
    const out: Particle[] = [];
    for (let i = 0; i < count; i++) {
      const angle = rand(i * 12.9898) * Math.PI * 2;
      const ox = (rand(i * 1.31) - 0.5) * 1.6;
      const oy = (rand(i * 2.71) - 0.5) * 0.55;
      const distance = 0.6 + rand(i * 7.13) * 1.6;
      const dx = Math.cos(angle) * distance;
      const dy = Math.sin(angle) * distance * 0.9 - 0.15;
      out.push({
        ox,
        oy,
        dx,
        dy,
        rot: (rand(i * 3.14) - 0.5) * 720,
        delay: rand(i * 5.55) * 0.35,
        size: 1 + rand(i * 9.19) * 2.2,
        alpha: 0.25 + rand(i * 11.37) * 0.75,
      });
    }
    return out;
  }, []);

  // Container size in px — measured once + on resize.
  const sizeRef = useRef({ w: 800, h: 200 });
  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const measure = () => {
      sizeRef.current = { w: el.clientWidth, h: el.clientHeight };
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Keep the target in sync with the prop each render.
  targetRef.current = clamp01(progress);

  // Imperative draw — runs continuously on rAF. Eases current → target so
  // scroll deltas that arrive in chunks (browsers batch scroll → React state)
  // are smoothed into a continuous 60fps animation.
  useLayoutEffect(() => {
    let raf = 0;
    let alive = true;

    const draw = () => {
      if (!alive) return;

      // Ease current toward target. 0.18 gives ~90ms half-life — visibly smooth
      // without introducing lag on fast scroll.
      const target = targetRef.current;
      const cur = currentRef.current;
      const next =
        Math.abs(target - cur) < 0.0005 ? target : cur + (target - cur) * 0.18;
      currentRef.current = next;

      const clamped = next;

      // Text layer: fade + gentle blur + slight scale-up while dissolving.
      const textOpacity = Math.max(0, 1 - clamped / 0.4);
      const textEl = textLayerRef.current;
      if (textEl) {
        textEl.style.opacity = String(textOpacity);
        textEl.style.filter = `blur(${clamped * 6}px)`;
        textEl.style.transform = `scale(${1 + clamped * 0.05})`;
      }

      // Particle layer visibility — hide DOM outright when idle to save paint.
      const layerEl = particleLayerRef.current;
      if (layerEl) {
        layerEl.style.opacity = clamped > 0.001 ? "1" : "0";
      }

      const { w, h } = sizeRef.current;
      // Skip per-particle math when the burst hasn't started.
      if (clamped > 0.001) {
        for (let i = 0; i < particles.length; i++) {
          const p = particles[i];
          const el = particleRefs.current[i];
          if (!el) continue;

          const local = Math.max(
            0,
            Math.min(1, (clamped - p.delay) / (1 - p.delay))
          );
          const ease = local * local * (3 - 2 * local); // smoothstep
          const startX = (p.ox * w) / 2 + w / 2;
          const startY = (p.oy * h) / 2 + h / 2;
          const endX = startX + (p.dx * w) / 2;
          const endY = startY + (p.dy * h) / 2;
          const x = startX + (endX - startX) * ease;
          const y = startY + (endY - startY) * ease;
          const fade =
            local < 0.05
              ? local / 0.05
              : Math.max(0, 1 - (local - 0.4) / 0.6);
          const opacity = fade * p.alpha;
          const rotation = p.rot * ease;

          // translate3d forces GPU compositing → far smoother than translate().
          el.style.transform = `translate3d(${x}px, ${y}px, 0) rotate(${rotation}deg)`;
          el.style.opacity = String(opacity);
        }
      }

      raf = requestAnimationFrame(draw);
    };

    raf = requestAnimationFrame(draw);
    return () => {
      alive = false;
      cancelAnimationFrame(raf);
    };
  }, [particles]);

  return (
    <div
      ref={containerRef}
      className={`relative inline-block ${className ?? ""}`}
    >
      {/* Text layer — animated imperatively */}
      <div
        ref={textLayerRef}
        style={{
          willChange: "opacity, filter, transform",
        }}
      >
        {children}
      </div>

      {/* Particle layer — rendered ONCE, transformed via refs each frame */}
      <div
        ref={particleLayerRef}
        className="pointer-events-none absolute inset-0 overflow-visible"
        style={{ opacity: 0, willChange: "opacity" }}
        aria-hidden="true"
      >
        {particles.map((p, i) => (
          <span
            key={i}
            ref={(el) => {
              particleRefs.current[i] = el;
            }}
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              width: `${p.size}px`,
              height: `${p.size}px`,
              borderRadius: "9999px",
              background: "#ffffff",
              boxShadow: `0 0 ${p.size * 1.5}px rgba(255,255,255,${p.alpha * 0.6})`,
              opacity: 0,
              willChange: "transform, opacity",
              transform: "translate3d(0, 0, 0)",
            }}
          />
        ))}
      </div>
    </div>
  );
}